import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { type MappedAssetSensor, mapAssetSensor } from '@/common/asset-sensor';
import { GoogleMapsProvider } from '@/components/map/GoogleMapsProvider';
import { useQueryListAssetSensorsOptions } from '@/queries/assets/sensors/list-sensors';
import { AbsoluteLoader } from '../../../absoluteLoader';
import { SensorGoogleMap } from './SensorGoogleMap';

export type SensorMapContainerProps = {
	assetId: string;
	selectedSensor: MappedAssetSensor | null;
	onSelectSensor: (sensor: MappedAssetSensor) => void;
};

export function SensorMapContainer({ assetId, onSelectSensor, selectedSensor }: SensorMapContainerProps) {
	const { t } = useTranslation();
	const { data, isLoading } = useQuery(useQueryListAssetSensorsOptions(assetId || ''));

	const sensors = data?.sensors || [];
	const mappedSensors = sensors.map(mapAssetSensor).filter((sensor) => sensor.coordinates !== null);

	useEffect(() => {
		if (selectedSensor) return;
		if (mappedSensors.length > 0) {
			onSelectSensor(mappedSensors[0]);
		}
	}, [mappedSensors, onSelectSensor, selectedSensor]);

	if (isLoading) {
		return (
			<div className="relative h-full w-full">
				<AbsoluteLoader className="bg-transparent" isLoading />
			</div>
		);
	}

	if (mappedSensors.length === 0) {
		return <div className="p-4">{t('health.sensors.noLocationData')}</div>;
	}

	return (
		<GoogleMapsProvider>
			<SensorGoogleMap sensors={mappedSensors} onSelectSensor={onSelectSensor} selectedSensor={selectedSensor} />
		</GoogleMapsProvider>
	);
}
